// The map file checker's building blocks, shared by mapfile.ts and each
// piece kind's own checks (prims/). It collects every problem in a file,
// each named by its path, rather than stopping at the first.

/** A color like "#aabbcc". */
export const COLOR = /^#[0-9a-f]{6}$/i;

export type Obj = Record<string, unknown>;

export class Checker {
  problems: string[] = [];

  fail(path: string, msg: string): void {
    this.problems.push(`${path}: ${msg}`);
  }

  obj(v: unknown, path: string): Obj | null {
    if (typeof v === "object" && v !== null && !Array.isArray(v)) return v as Obj;
    this.fail(path, "expected an object");
    return null;
  }

  num(o: Obj, key: string, path: string, opts: { min?: number; max?: number; optional?: boolean } = {}): number {
    const v = o[key];
    if (v === undefined && opts.optional) return 0;
    if (typeof v !== "number" || !Number.isFinite(v)) {
      this.fail(`${path}.${key}`, "expected a number");
      return 0;
    }
    if (opts.min !== undefined && v < opts.min) this.fail(`${path}.${key}`, `must be at least ${opts.min}`);
    if (opts.max !== undefined && v > opts.max) this.fail(`${path}.${key}`, `must be at most ${opts.max}`);
    return v;
  }

  str(o: Obj, key: string, path: string, pattern?: RegExp): string {
    const v = o[key];
    if (typeof v !== "string" || v.length === 0) {
      this.fail(`${path}.${key}`, "expected a non-empty string");
      return "";
    }
    if (pattern && !pattern.test(v)) this.fail(`${path}.${key}`, `"${v}" doesn't match ${pattern}`);
    return v;
  }

  color(o: Obj, key: string, path: string): string {
    return this.str(o, key, path, COLOR);
  }

  /** Complains about keys the format doesn't know, which are usually typos. */
  only(o: Obj, keys: string[], path: string): void {
    for (const k of Object.keys(o)) if (!keys.includes(k)) this.fail(`${path}.${k}`, "unknown field");
  }
}
